/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import TrackingService from './TrackingService';

export class LearnTrackingService extends TrackingService implements ILearnTrackingService {
	/**
	 * Returns an array of playlist resources whose CompletionStatus property matches `completionStatus`.
	 * The returned array is NOT guaranteed to be sorted in any way.
	 * @param {ResourceArray} playlistIdArray - array of playlist IDs
	 * @param {CompletionStatus} completionStatus - playlist CompletionStatus to match
	 * @returns {ResourceArray} An array of playlist resources whose CompletionStatus property matches `completionStatus`
	 */
	async getPlaylistsForCompletionStatus(
		playlistIdArray: ResourceArray,
		completionStatus: CompletionStatus,
	): Promise<ResourceArray> {
		const playlistResourceCategoryArray: ResourceCategoryArray = playlistIdArray.map(resource => ({
			resource,
			category: 'playlist',
		}));

		// Make sure we get track info for these playlists into our cache
		const responseArray = await this.getTrackInfo(playlistResourceCategoryArray);

		const resultArray: Array<string> = [];

		responseArray.forEach((trackInfoItem: TrackInfoItem) => {
			// Looks for tracks whose CompletionStatus matches the specified CompletionStatus
			if (trackInfoItem) {
				const itemCompletionStatus: CompletionStatus | null | undefined = trackInfoItem.completionStatus;
				if (itemCompletionStatus === completionStatus) {
					resultArray.push(trackInfoItem.resource);
				}
			}
			else {
				// Unexpected: This TrackInfoItem is undefined, meaning getTrackInfo failed to create a dummy, possibly due to _findNextEmpty()
				this._logMessage('undefined TrackInfoItem in getPlaylistsForCompletionStatus', undefined);
			}
		});

		return resultArray;
	}

	/**
	 * Returns 1) the percentage of resources in the specified array that have been viewed,
	 * and 2) the time of the most recently viewed resource.
	 * NOTE - If none of the resources have been viewed, `lastModified` will be undefined.
	 * NOTE - This first checks if the specified playist has been tracked, and if so, whether it has viewed
	 * @param {string} playlistId - a playlist ID
	 * @param {ResourceArray} tutorialIdArray - an array of tutorial IDs
	 * @returns {PvLmPair} An object containing the time of the most-recently viewed resource and the percentage of resources that have been viewed.
	 */
	async getPercentViewedAndLastModified(playlistId: string, tutorialIdArray: ResourceArray): Promise<PvLmPair> {
		const result: PvLmPair = {
			lastModified: undefined,
			percentViewed: 0,
		};

		if (playlistId && playlistId.length > 0 && tutorialIdArray && tutorialIdArray.length > 0) {
			// Get track info on the playlist
			const completionStatusTimePair = await this.getCompletionStatus({
				resource: playlistId,
				category: 'playlist',
			});

			// If the user has made progress on tutorial(s) in this playlist, then get the time/percentage info
			if (
				completionStatusTimePair
				&& (completionStatusTimePair.status === 'partial' || completionStatusTimePair.status === 'complete')
			) {
				// Get track info on the tutorials in this playlist
				const tutorialResourceCategoryArray: ResourceCategoryArray = tutorialIdArray.map(resource => ({
					resource,
					category: 'tutorial',
				}));

				// Make sure these are in the cache
				await this.getTrackInfo(tutorialResourceCategoryArray);

				// For each resource in the input tutorial array,
				// build an array of track info items (returned from the cache).
				const infoArray = tutorialIdArray.map((resource: string) => TrackingService.cacheInstance[resource]);
				const totalCount = infoArray.length;
				let viewedCount = 0;
				let mostRecentlyViewedTimestamp = 0;
				infoArray.forEach((info) => {
					if (info && this._isTutorialViewed(info)) {
						// Checks `info`, too, in case it is undefifined
						++viewedCount;

						/* istanbul ignore else */
						if (info.viewedTime > mostRecentlyViewedTimestamp) {
							mostRecentlyViewedTimestamp = info.viewedTime;
						}
					}
				});

				/* istanbul ignore else */
				if (viewedCount > 0) {
					result.lastModified = mostRecentlyViewedTimestamp;
				}
				result.percentViewed = viewedCount / totalCount;
			}
			else {
				// User has made no progress on this playlist, so ignore it
			}
		}
		else {
			this._logMessage('invalid parameters in getPercentViewedAndLastModified', undefined);
		}

		return result;
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */

	/**
	 * By Brady's new logic for allowing a user to 'unmark' tutorials 'completed'/'incomplete' on mobile, we must account for the new logic with determining if a tutorial truly is 'viewed' or not.
	 * @returns {bool} - true, if the tutorial is marked as 'completed', false, if tutorial is marked as 'incomplete'
	 */
	_isTutorialViewed(trackInfo: TrackInfoItem): boolean {
		const tutViews = trackInfo.viewed;
		const tutCompStatus = trackInfo.completionStatus;

		let tutIsViewed = false; // todo: ask allan about better way to do this other than very explicitly setting each condition
		if (
			(tutViews > 0 && tutCompStatus === 'complete')
			|| (tutViews > 0 && tutCompStatus === 'none')
			|| (tutViews > 0 && tutCompStatus === undefined)
			|| (tutViews === 0 && tutCompStatus === 'complete')
		) {
			tutIsViewed = true;
		}
		// Leaving below commented so we're clear about what makes a false case
		// else if ((tutViews > 0 && tutCompStatus === 'partial') || (tutViews === 0 && tutCompStatus === 'partial') || (tutIsViewed === 0 && tutCompStatus === 'none') || (tutIsViewed === 0 && tutCompStatus === undefined)) {
		// 	tutIsViewed = false;
		// }
		return tutIsViewed;
	}
}

export default LearnTrackingService;
